/**
 * USP Section - Newfive-inspired layout
 * Left: label, heading, description, buttons
 * Below or right: checkmark USP items
 * Right (desktop): optional image
 */

import { CheckCircle2, ArrowRight, TrendingUp, RefreshCw, BarChart3, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import avatarBril from "@/assets/avatars/avatar-bril.png.asset.json";
import avatarSkincare from "@/assets/avatars/avatar-skincare.png.asset.json";
import avatarVrouw from "@/assets/avatars/avatar-vrouw.png.asset.json";
import avatarHybforce from "@/assets/portfolio/hybforce-founder.png.asset.json";

const ctaAvatars = [
  { src: avatarBril.url, alt: "Webshopeigenaar", pos: "object-center" },
  { src: avatarSkincare.url, alt: "Webshopeigenaar", pos: "object-[50%_18%]" },
  { src: avatarVrouw.url, alt: "Webshopeigenaar", pos: "object-[50%_25%]" },
  { src: avatarHybforce.url, alt: "Hybforce", pos: "object-center" },
];


const tile = "h-12 min-w-12 px-3.5 rounded-2xl bg-white text-primary text-sm font-semibold flex items-center justify-center";
const tileAccent = "h-12 min-w-12 px-3.5 rounded-2xl bg-accent text-accent-foreground text-sm font-semibold flex items-center justify-center";
const pill = "h-12 px-4 rounded-2xl bg-white text-primary text-sm font-semibold flex items-center gap-2";


const uspGlyphs: React.ReactNode[] = [
  // Hogere conversie
  <>
    <span className={tile}>1,2%</span>
    <ArrowRight className="w-5 h-5 text-accent" />
    <span className={tileAccent}>3,4%</span>
  </>,
  // Hogere orderwaarde
  <>
    <span className={pill}>AOV</span>
    <span className={tileAccent}>+21%</span>
  </>,
  // Winstgevende groei
  <>
    <span className={tile}>ROI</span>
    <span className={tileAccent}>
      <TrendingUp className="w-5 h-5" />
    </span>
    
  </>,
  // Doorlopende optimalisatie
  <>
    <span className={pill}>
      <RefreshCw className="w-3.5 h-3.5 text-accent" /> A/B test
    </span>
    <span className={tileAccent}>
      <BarChart3 className="w-5 h-5" />
    </span>
  </>,
];



export interface USPItem {
  label?: string;
  title: string;
  description: string;
}

export interface USPSectionProps {
  label?: string;
  title: string;
  description: string;
  primaryButton?: { text: string; href?: string; onClick?: () => void; };
  secondaryButton?: { text: string; href: string; };
  usps: USPItem[];
  image?: string;
  imageMobile?: string;
  imageAlt?: string;
  imageFit?: "cover" | "contain";
  /** When set, renders the left CTA card (text + logos + button) and a 2x2 USP grid on the right */
  ctaCard?: { text: React.ReactNode; button: { text: string; href?: string; onClick?: () => void; }; };
}

export default function USPSection({
  label,
  title,
  description,
  primaryButton,
  secondaryButton,
  usps,
  image,
  imageMobile,
  imageAlt = "",
  imageFit = "cover",
  ctaCard
}: USPSectionProps) {
  if (ctaCard) {
    return (
      <section className="px-4 md:px-12 py-10 md:py-16 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="rounded-[2rem] md:rounded-[2.5rem] bg-cream px-6 py-10 md:px-14 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
              {/* Left */}
              <div className="flex flex-col">
                {label && <p className="text-accent font-semibold text-sm mb-4">{label}</p>}
                <h2 className="text-3xl md:text-4xl lg:text-[2.5rem] font-bold text-primary leading-[1.15] mb-5">
                  {title}
                </h2>
                <p className="text-base md:text-lg leading-relaxed mb-8 max-w-md text-primary/70 whitespace-pre-line">
                  {description}
                </p>

                {/* CTA card */}
                <div className="rounded-[1.75rem] bg-primary text-white p-7 md:p-9">
                  <p className="text-sm md:text-base leading-relaxed text-white/80 mb-8">
                    {ctaCard.text}
                  </p>

                  <div className="flex items-center gap-3 mb-8 flex-wrap">
                    <div className="flex -space-x-2.5">
                      {ctaAvatars.map((a) => (
                        <img
                          key={a.src}
                          src={a.src}
                          alt={a.alt}
                          className={`w-9 h-9 rounded-full object-cover ring-2 ring-primary ${a.pos}`}
                          loading="lazy"
                          decoding="async"
                        />
                      ))}
                    </div>
                    <Star className="w-4 h-4 fill-accent text-accent" />
                    <span className="text-sm font-semibold text-white">5,0</span>
                    <span className="text-sm text-white/70">Vertrouwd door meer dan 30+ webshopeigenaren</span>
                  </div>


                  {ctaCard.button.onClick ? (
                    <Button
                      onClick={ctaCard.button.onClick}
                      className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold h-14 rounded-2xl text-sm md:text-base gap-2"
                    >
                      {ctaCard.button.text}
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button asChild className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold h-14 rounded-2xl text-sm md:text-base gap-2">
                      <Link to={ctaCard.button.href!}>
                        {ctaCard.button.text}
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </Button>
                  )}
                </div>
              </div>

              {/* Right: 2x2 grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-12 md:gap-x-10 md:gap-y-16">
                {usps.map((usp, i) => (
                  <div key={i} className="flex flex-col items-center text-center">
                    <div className="mb-6 flex items-center justify-center gap-2">
                      {uspGlyphs[i % uspGlyphs.length]}
                    </div>
                    <h3 className="font-bold text-lg mb-3 text-primary">{usp.title}</h3>
                    <p className="text-sm md:text-[0.95rem] leading-relaxed text-primary/60 max-w-[17rem]">{usp.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

    );
  }


  return (
    <section className="px-4 md:px-12 py-6 md:py-10 lg:py-16 bg-cream">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10 lg:gap-12 xl:gap-16">
          {/* Left: content + USPs */}
          <div className="flex flex-col">
            {label &&
            <p className="text-accent font-semibold text-sm mb-3">{label}</p>
            }
            <h2 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-primary leading-tight mb-5">
              {title}
            </h2>
            <p className="text-base md:text-lg leading-relaxed mb-6 max-w-xl text-primary whitespace-pre-line">
              {description}
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-8">
              {primaryButton &&
                (primaryButton.onClick ? (
                  <Button
                    className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-5 md:px-7 h-11 md:h-12 rounded-full text-sm md:text-base"
                    onClick={primaryButton.onClick}
                  >
                    {primaryButton.text}
                  </Button>
                ) : (
                  <Button asChild className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-5 md:px-7 h-11 md:h-12 rounded-full text-sm md:text-base">
                    <Link to={primaryButton.href!}>{primaryButton.text}</Link>
                  </Button>
                ))
              }
              {secondaryButton &&
              <Button asChild variant="ghost" className="text-primary hover:text-accent font-medium gap-1.5 text-sm md:text-base">
                  <Link to={secondaryButton.href}>
                    {secondaryButton.text}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              }
            </div>

            {/* USP items under text */}
            <div className="space-y-4">
              {usps.map((usp, i) => {
                const isDark = i % 2 === 0;
                return (
                <div
                  key={i}
                  className={`group relative rounded-2xl p-6 md:p-7 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 overflow-hidden ${
                    isDark
                      ? "bg-primary text-white hover:shadow-primary/20"
                      : "bg-muted/40 border border-border/60 hover:shadow-primary/5 hover:border-accent/30"
                  }`}>
                
                    {/* Large background number */}
                    <span className={`absolute -top-2 -right-1 text-[5.5rem] font-black leading-none select-none pointer-events-none transition-colors duration-500 ${
                      isDark ? "text-white/[0.06] group-hover:text-accent/[0.12]" : "text-primary/[0.04] group-hover:text-accent/[0.08]"
                    }`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>

                    {/* Accent line */}
                    <div className="w-8 h-1 rounded-full bg-accent/60 mb-4 transition-all duration-500 group-hover:w-14 group-hover:bg-accent" />

                    {usp.label && (
                      <p className={`text-xs font-semibold uppercase tracking-wide mb-1 relative z-10 ${isDark ? "text-accent" : "text-accent"}`}>
                        {usp.label}
                      </p>
                    )}
                    <h3 className={`font-bold text-base md:text-lg mb-1.5 relative z-10 ${isDark ? "text-white" : "text-primary"}`}>{usp.title}</h3>

                    <p className={`text-sm leading-relaxed relative z-10 ${isDark ? "text-white/80" : "text-muted-foreground"}`}>{usp.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: image - fits within left column height */}
          {image && (
            imageFit === "contain" ? (
              <>
                <div className="relative min-h-[300px] lg:min-h-0 lg:self-stretch hidden md:flex items-center justify-center">
                  <img
                    src={image}
                    alt={imageAlt}
                    className="w-full h-full object-contain lg:max-h-[90%] lg:max-w-[90%]"
                    loading="lazy"
                    decoding="async" />
                </div>
                <div className="relative min-h-[300px] lg:min-h-0 lg:self-stretch flex md:hidden items-center justify-center">
                  <img
                    src={imageMobile || image}
                    alt={imageAlt}
                    className="w-full h-full object-contain"
                    loading="lazy"
                    decoding="async" />
                </div>
              </>
            ) : (
              <>
                <div className="relative min-h-[300px] lg:min-h-0 lg:self-stretch hidden md:block">
                  <img
                    src={image}
                    alt={imageAlt}
                    className="w-full h-full object-cover rounded-2xl shadow-lg absolute inset-0"
                    loading="lazy"
                    decoding="async" />
                </div>
                <div className="relative min-h-[300px] lg:min-h-0 lg:self-stretch md:hidden">
                  <img
                    src={imageMobile || image}
                    alt={imageAlt}
                    className="w-full h-full object-cover rounded-2xl shadow-lg absolute inset-0"
                    loading="lazy"
                    decoding="async" />
                </div>
              </>
            )
          )}
        </div>
      </div>
    </section>);

}